import React, { useContext, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import generateString from '../../utils/randomStringGenerator';
import namingValidator from '../../utils/routerConfig/namingValidator';
import useGlynt from '../hooks/useGlynt';
import { AppContext } from '../../App';
import { AxiosError } from 'axios';
import { IDataPool, INamingOptions } from '../../types/my-types';
import {
  aggregratingOptionsList,
  dailyCronSchedule,
  namingOptionsList,
  publishDateOptionsList,
  routerConfigJSONRequestBuilder,
  scheduleOptions,
  separatorOptions,
  weeklyCronSchedule,
} from '../../utils/routerConfig/routerConfigHelper';
import { useGetRouterConfig } from '../hooks/useGetRouterConfig';
import { useMutation } from '@tanstack/react-query';

interface RouterConfigSettingsProps {
  dataPool: IDataPool;
  open?: boolean;
  onDialogClose?: () => void;
}

interface RouterConfigSettingsState {
  schedule: string;
  aggregateOption: string;
  namingOptions: INamingOptions[];
  namingOptionsListCount: number;
  errors: string[];
  routerPrefix: string;
  separator: string;
}

// FIXME: Move styles to central location

function RouterConfigSettings({ dataPool, open: controlledOpen, onDialogClose }: RouterConfigSettingsProps) {
  const labelValue = dataPool ? dataPool.label : '';

  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = React.useState(false);
  const open = isControlled ? controlledOpen : internalOpen;

  const setOpen = (value: boolean) => {
    if (!isControlled) setInternalOpen(value);
    if (!value) onDialogClose?.();
  };
  const [randomIdString] = useState(generateString(6));

  const [configState, setConfigState] = useState<RouterConfigSettingsState>({
    schedule: '',
    aggregateOption: '',
    namingOptions: [],
    namingOptionsListCount: 1,
    errors: [],
    routerPrefix: '',
    separator: '-',
  });

  const glynt = useGlynt();

  const { setToast } = useContext(AppContext);

  const scheduleOptionsIndex = scheduleOptions.findIndex(
    (option) => option.key === configState.schedule
  );

  const { mutateAsync: saveRouterConfig } = useMutation({
    mutationFn: async (params: any) => {
      setConfigState({
        ...configState,
        errors: [],
      });
      return await (await glynt).dataPool.updateRouterConfig(dataPool.id, params);
    },

    onSuccess: () => {
      setToast({
        open: true,
        message: 'Router config saved successfully.',
        variant: 'success',
      });

      setOpen(false);
    },

    onError: (error: AxiosError) => {
      let message = error.message || 'Failed to save router config.';
      setToast({
        variant: 'error',
        open: true,
        message,
      });
    }
  });

  const filterNamingOptions = (option: { key: string; title: string }) => {
    // Remove document name if schedule is not per document
    if (
      option.key === 'document_name' &&
      configState.schedule !== 'per_document' &&
      configState.aggregateOption !== 'one_data_file_per_document'
    ) {
      return false;
    } else if (
      option.key === 'training_set_label' &&
      configState.aggregateOption === 'all_documents_in_one_data_file'
    ) {
      return false;
    }
    return true;
  };

  const isNamingOptionsValid = () => {
    if (configState.aggregateOption === 'one_data_file_per_training_set') {
      // Make sure document name is not present in one_data_file_per_training_set

      for (let i = 0; i < configState.namingOptions.length; i++) {
        if (configState.namingOptions[i]?.type === 'document_name') {
          return false;
        }
      }
    } else if (configState.aggregateOption === 'all_documents_in_one_data_file') {
      // Make sure training set name and document name are not present in all_documents_in_one_data_file

      for (let i = 0; i < configState.namingOptions.length; i++) {
        if (
          configState.namingOptions[i]?.type === 'document_name' ||
          configState.namingOptions[i]?.type === 'training_set_label'
        ) {
          return false;
        }
      }
    }

    return true;
  };

  const isFormValid = () => {
    // FIXME: Check to see if data has changed from API if set...

    if (!configState.schedule) {
      return false;
    }

    if (!configState.separator || configState.separator.length > 1) {
      return false;
    }

    if (configState.schedule !== 'per_document' && !configState.aggregateOption) {
      return false;
    }

    if (configState.namingOptions.length === 0) {
      return false;
    }

    for (let i = 0; i < configState.namingOptions.length; i++) {
      if (
        !namingValidator(configState.namingOptions[i]?.type, configState.namingOptions[i]?.value)
      ) {
        return false;
      }
    }

    if (!isNamingOptionsValid()) {
      return false;
    }

    return true;
  };

  const {
    data: routeConfigData,
    isLoading: isLoadingRouteConfig,
    isFetching: isFetchingRouteConfig,
    isSuccess: isSuccessRouteConfig,
  } = useGetRouterConfig(dataPool.id, open);

  // Set correct state values for the UI based upon the data from the server
  useEffect(() => {
    let updatedSchedule = null;
    let updatedAggregateOption = null;
    let namingOptionsFromServer: INamingOptions[] = [];
    let errors = [];
    let updatedRouterPrefix = '';
    let updatedSeparator = null;

    if (routeConfigData) {
      // Get the router prefix

      if (routeConfigData?.data_packaging_config?.separator) {
        updatedSeparator = routeConfigData?.data_packaging_config?.separator;
      }

      if (routeConfigData?.route_prefix?.prefix) {
        updatedRouterPrefix = routeConfigData?.route_prefix?.prefix;
      }
      // Set how often you want to publish the data
      if (routeConfigData?.package_trigger?.type === 'verification') {
        updatedSchedule = 'per_document';
      } else if (routeConfigData?.package_trigger?.config?.expressions[0] === dailyCronSchedule) {
        updatedSchedule = 'daily';
      } else if (routeConfigData?.package_trigger?.config?.expressions[0] === weeklyCronSchedule) {
        updatedSchedule = 'weekly';
      } else {
        errors.push('Unsupported schedule from server');
      }

      // Set aggregate option
      if (!updatedSchedule) {
        errors.push('Unsupported schedule from server');
      } else if (updatedSchedule === 'per_document') {
        // only option because the per_document doesn't allow any aggregation for document
      } else if (
        routeConfigData?.data_packaging_config?.package_format?.type == 'one_data_file_per_document'
      ) {
        updatedAggregateOption = 'one_data_file_per_document';
      } else if (
        routeConfigData?.data_packaging_config?.package_format?.type === 'aggregated_data_file' &&
        (routeConfigData?.data_packaging_config?.package_format?.options?.lookup_value ===
          'document.id' ||
          routeConfigData?.data_packaging_config?.package_format?.options?.lookup_value ===
            'document.name')
      ) {
        updatedAggregateOption = 'one_data_file_per_document';
      } else if (
        routeConfigData?.data_packaging_config?.package_format?.type === 'aggregated_data_file' &&
        routeConfigData?.data_packaging_config?.package_format?.options?.lookup_value ===
          'published_at'
      ) {
        updatedAggregateOption = 'all_documents_in_one_data_file';
      } else if (
        routeConfigData?.data_packaging_config?.package_format?.type === 'aggregated_data_file' &&
        routeConfigData?.data_packaging_config?.package_format?.options?.lookup_value ===
          'training_set_id'
      ) {
        updatedAggregateOption = 'one_data_file_per_training_set';
      } else {
        errors.push('Unsupported aggregate option from server');
      }

      // Set naming formula
      if (routeConfigData?.data_packaging_config?.naming_formula) {
        routeConfigData?.data_packaging_config?.naming_formula.map((formula: any) => {
          if (typeof formula === 'string') {
            namingOptionsFromServer.push({
              type: 'string_literal',
              value: formula,
            });
          } else if (formula?.lookup_value === 'document.name') {
            namingOptionsFromServer.push({ type: 'document_name' });
          } else if (formula?.lookup_value === 'training_set_label') {
            namingOptionsFromServer.push({ type: 'training_set_label' });
          } else if (formula?.lookup_value === 'published_at') {
            if (formula?.configuration?.strftime === '%Y-%m-%d') {
              namingOptionsFromServer.push({ type: 'publish_date', value: 'YYYY-MM-DD' });
            } else {
              namingOptionsFromServer.push({ type: 'publish_date', value: 'YYYY-MM' });
            }
          } else {
            errors.push('Unsupported naming formula from server');
          }
        });
      }

      setConfigState({
        ...configState,
        ...(updatedSchedule && { schedule: updatedSchedule }),
        ...(updatedAggregateOption && { aggregateOption: updatedAggregateOption }),
        ...(namingOptionsFromServer.length > 0 && { namingOptions: namingOptionsFromServer }),
        routerPrefix: updatedRouterPrefix,
        ...(updatedSeparator && { separator: updatedSeparator }),
        namingOptionsListCount:
          namingOptionsFromServer.length > 0 ? namingOptionsFromServer.length : 1,
        errors: errors,
      });
    }
  }, [routeConfigData, isSuccessRouteConfig, isFetchingRouteConfig, isLoadingRouteConfig]);

  const handleSubmit = async () => {
    if (!isFormValid()) {
      setToast({
        variant: 'error',
        open: true,
        message: 'Form is not valid',
      });

      return;
    }

    try {
      await saveRouterConfig(
        routerConfigJSONRequestBuilder(
          configState.schedule,
          configState.aggregateOption,
          configState.namingOptions,
          configState.routerPrefix,
          configState.separator
        )
      );
    } catch (error) {
      console.error('Error saving router config', error);
    }
  };

  const handleClose = () => {
    setOpen(false);

    setConfigState({
      schedule: '',
      aggregateOption: '',
      namingOptions: [],
      namingOptionsListCount: 1,
      errors: [],
      routerPrefix: '',
      separator: '-',
    });
  };

  return (
    <>
      <Dialog maxWidth="sm" fullWidth open={open} onClose={handleClose}>
        <DialogTitle>Outbound Routing Config for {dataPool.label}</DialogTitle>
        <DialogContent>
          {isLoadingRouteConfig || isFetchingRouteConfig ? (
            <p>Loading...</p>
          ) : (
            <>
              <DialogContentText>Router Prefix</DialogContentText>
              <TextField
                autoFocus
                margin="dense"
                data-cy={`input-router-prefix-${dataPool.label}`}
                variant="outlined"
                id="router-tags"
                // label="Router Prefix Settings"
                placeholder="sample_prefix/123"
                disabled={isLoadingRouteConfig || isFetchingRouteConfig}
                value={configState.routerPrefix}
                type="text"
                onChange={(e: any) => {
                  setConfigState({
                    ...configState,
                    routerPrefix: e.target.value,
                    errors: [],
                  });
                }}
                helperText={'Set the route prefix on this data pool.'}
              />
              <Divider style={{ margin: '1rem 0' }} />
              <DialogContentText>How often do you want to publish the data?</DialogContentText>
              <Select
                data-cy="select-route-config-schedule"
                label="Published Date Options"
                name="publishedDate"
                value={configState.schedule}
                style={{ maxWidth: '250px' }}
                onChange={(e: any) => {
                  setConfigState({
                    ...configState,
                    schedule: e.target.value,
                    ...(e.target.value === 'per_document' && { aggregateOption: '' }),
                    errors: [],
                  });
                }}
                fullWidth
              >
                {scheduleOptions?.map((option, index) => (
                  <MenuItem
                    key={`schedule-options-${index}`}
                    value={option.key}
                    data-cy={`select-route-config-schedule-${option.key}`}
                  >
                    {option.title}
                  </MenuItem>
                ))}
              </Select>
              {configState.schedule && scheduleOptions[scheduleOptionsIndex].description ? (
                <Typography style={{ fontSize: '12px' }}>
                  {scheduleOptions[scheduleOptionsIndex].description}
                </Typography>
              ) : null}
              {configState.schedule !== '' && configState.schedule !== 'per_document' ? (
                <>
                  <Divider style={{ margin: '1rem 0' }} />

                  <DialogContentText>
                    How do you want data aggregated into data files?
                  </DialogContentText>
                  {/*
                  One data file per document
                  All documents in one data file
                  One data file per training set
                */}
                  <Select
                    data-cy="select-route-config-aggregate-option"
                    label="Aggregate Options"
                    name="aggregateOption"
                    value={configState.aggregateOption}
                    style={{ maxWidth: '250px' }}
                    error={!configState.aggregateOption}
                    // FIXME: type the change event
                    onChange={(e: any) => {
                      setConfigState({
                        ...configState,
                        aggregateOption: e.target.value,
                        errors: [],
                      });
                    }}
                    disabled={configState.schedule === 'per_document'}
                    fullWidth
                  >
                    {aggregratingOptionsList?.map((option, index) => (
                      <MenuItem
                        key={`aggregate-options-${index}`}
                        value={option.key}
                        data-cy={`select-route-config-aggregate-option-${option.key}`}
                      >
                        {option.title}
                      </MenuItem>
                    ))}
                  </Select>
                </>
              ) : null}
              <Divider style={{ margin: '1rem 0' }} />
              <DialogContentText>How do you want to name the data files?</DialogContentText>
              {/*
            String literal
            Document name
            Publish date (YYYY-MM, YYYY-MM-DD)
            */}
              {[...Array(configState.namingOptionsListCount)].map((_, i) => {
                return (
                  <React.Fragment key={`naming-options-list-${i}`}>
                    <div
                      style={{
                        marginBottom: '15px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div style={{ display: 'flex' }}>
                        <Select
                          data-cy={`select-route-config-naming-formula-${i}`}
                          name="namingFormula"
                          value={configState.namingOptions[i]?.type || ''}
                          style={{ minWidth: '200px', maxWidth: '200px' }}
                          // FIXME: type the change event
                          onChange={(e: any) => {
                            setConfigState({
                              ...configState,
                              errors: [],
                              namingOptions: configState.namingOptions
                                .map((option, index) => {
                                  if (index === i) {
                                    return { type: e.target.value };
                                  }
                                  return option;
                                })
                                .concat(
                                  i === configState.namingOptions.length
                                    ? [{ type: e.target.value }]
                                    : []
                                ),
                            });
                          }}
                          fullWidth
                        >
                          {namingOptionsList?.filter(filterNamingOptions).map((option, index) => {
                            return (
                              <MenuItem
                                key={`naming-options-${index}`}
                                value={option.key}
                                data-cy={`select-route-config-naming-formula-${option.key}`}
                              >
                                {option.title}
                              </MenuItem>
                            );
                          })}
                        </Select>
                        {configState.namingOptions[i]?.type === 'string_literal' ? (
                          <TextField
                            autoFocus
                            size="small"
                            margin="dense"
                            variant="outlined"
                            label="String Literal"
                            placeholder="String Literal"
                            type="text"
                            value={configState.namingOptions[i]?.value || ''}
                            style={{ marginLeft: '10px', marginTop: '0' }}
                            data-cy={`input-route-config-naming-formula-${i}-string-literal`}
                            error={
                              !namingValidator(
                                configState.namingOptions[i]?.type,
                                configState.namingOptions[i]?.value
                              )
                            }
                            // FIXME: Type event
                            onChange={(e: any) => {
                              setConfigState({
                                ...configState,
                                errors: [],
                                namingOptions: [
                                  ...configState.namingOptions.slice(0, i),
                                  {
                                    type: 'string_literal',
                                    value: e.target.value,
                                  },
                                  ...configState.namingOptions.slice(i + 1),
                                ],
                              });
                            }}
                          />
                        ) : null}
                        {configState.namingOptions[i]?.type === 'publish_date' ? (
                          <>
                            <Select
                              data-cy={`select-route-config-naming-formula-${i}-publish-date`}
                              label="Published Date Options"
                              name="publishedDate"
                              value={configState.namingOptions[i].value || ''}
                              style={{ minWidth: '200px', maxWidth: '200px', marginLeft: '10px' }}
                              error={
                                !namingValidator(
                                  configState.namingOptions[i]?.type,
                                  configState.namingOptions[i]?.value
                                )
                              }
                              // FIXME: type the change event
                              onChange={(e: any) => {
                                setConfigState({
                                  ...configState,
                                  errors: [],
                                  namingOptions: configState.namingOptions.map((option, index) => {
                                    if (index === i) {
                                      return { ...option, value: e.target.value };
                                    }
                                    return option;
                                  }),
                                });
                              }}
                              fullWidth
                            >
                              {publishDateOptionsList?.map((option, index) => (
                                <MenuItem
                                  key={`published-date-${index}`}
                                  value={option.key}
                                  data-cy={`select-route-config-naming-formula-${i}-publish-date-${option.key}`}
                                >
                                  {option.title}
                                </MenuItem>
                              ))}
                            </Select>
                          </>
                        ) : null}
                      </div>
                      <div>
                        {configState.namingOptionsListCount > 1 ? (
                          <DeleteIcon
                            data-cy={`btn-route-config-naming-formula-delete-${i}`}
                            onClick={() => {
                              setConfigState({
                                ...configState,
                                errors: [],
                                namingOptions: [
                                  ...configState.namingOptions.slice(0, i),
                                  ...configState.namingOptions.slice(i + 1),
                                ],
                                namingOptionsListCount: configState.namingOptionsListCount - 1,
                              });
                            }}
                            style={{ cursor: 'pointer', color: 'red' }}
                          />
                        ) : (
                          <div style={{ width: '20px', display: 'inline-block' }}> </div>
                        )}

                        {i === configState.namingOptionsListCount - 1 ? (
                          <AddCircleIcon
                            onClick={() => {
                              if (
                                !namingValidator(
                                  configState.namingOptions[i]?.type,
                                  configState.namingOptions[i]?.value
                                )
                              )
                                return;

                              setConfigState({
                                ...configState,
                                errors: [],
                                namingOptionsListCount: configState.namingOptionsListCount + 1,
                              });
                            }}
                            data-cy={`btn-route-config-naming-formula-add-${i}`}
                            style={{
                              cursor: 'pointer',
                              color: !namingValidator(
                                configState.namingOptions[i]?.type,
                                configState.namingOptions[i]?.value
                              )
                                ? 'grey'
                                : 'green',
                              marginLeft: '5px',
                            }}
                          />
                        ) : (
                          <div style={{ width: '29px', display: 'inline-block' }}> </div>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
              <DialogContentText>Separator</DialogContentText>
              <Select
                data-cy={`select-route-config-separator`}
                label="Separator"
                name="separator"
                value={configState.separator}
                style={{ minWidth: '50px', maxWidth: '50px' }}
                error={!configState.separator || configState.separator.length > 1}
                // FIXME: type the change event
                onChange={(e: any) => {
                  setConfigState({
                    ...configState,
                    separator: e.target.value,
                    errors: [],
                  });
                }}
                fullWidth
              >
                {separatorOptions?.map((option, index) => (
                  <MenuItem
                    key={`separator-${index}`}
                    value={option}
                    data-cy={`select-route-config-separator-${index}-${option}`}
                  >
                    {option}
                  </MenuItem>
                ))}
              </Select>
              <br />
              <br />
              <Typography>
                Your filename will look like this:
                <br />
                {!isNamingOptionsValid() ? (
                  <span style={{ color: 'red', fontSize: '12px' }}>
                    There are errors in the naming options formula. Please review and fix.
                  </span>
                ) : (
                  <span data-cy="span-route-config-sample-file-name">
                    {configState.namingOptions.length > 0 &&
                      configState.namingOptions.map((option, index) => {
                        let ret: any = '';
                        if (option?.type === 'document_name') {
                          ret = 'DocumentName';
                        } else if (option?.type === 'training_set_label') {
                          ret = 'TrainingSetName';
                        } else {
                          ret = option?.value || '';
                        }
                        if (index !== configState.namingOptions.length - 1) {
                          ret += configState.separator;
                        }

                        return ret;
                      })}
                  </span>
                )}
                {isNamingOptionsValid() && configState.namingOptions.length > 0
                  ? `${configState.separator}${randomIdString}`
                  : ''}
              </Typography>
              <Divider style={{ margin: '1rem 0' }} />
            </>
          )}

          {configState.errors.length > 0 ? (
            <div style={{ color: 'red', marginBottom: '1rem' }}>
              <Typography>Errors: {configState.errors.join(', ')}</Typography>
              <Typography>Please contact support or reset the router config options.</Typography>
            </div>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary" data-cy="btn-route-config-cancel">
            Cancel
          </Button>
          <Button
            data-cy="btn-route-config-submit"
            onClick={handleSubmit}
            color="primary"
            disabled={!isFormValid() || isLoadingRouteConfig || isFetchingRouteConfig}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default RouterConfigSettings;
